import { useApi } from '@/api';
import {
  ActivityCard,
  EntityName,
  ExternalLink,
  KpiCard,
  Link,
  LinkBanner,
  Page,
  PageSkeleton,
  InfoTooltip,
  LinkButton,
  ExternalLinkIcon
} from '@/components';
import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  Box,
  CryptoIcon,
  EtherScanPictogram,
  Flex,
  Heading,
  LinkWrapper
} from '@blockanalitica/ui';
import HistoricTabs from './HistoricTabs';
import HoldersDonutChart from './HoldersDonutChart';
import TopSuppliers from './TopSuppliers';
import TopVaults from './TopVaults';
import VaultsDonutChart from './VaultsDonutChart';
// import IrmChart from './IrmChart';

export default function STUSDSPage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: `/stusds/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page
        headerFlexDirection={['column', 'row']}
        title={
          <EntityName
            symbol1={
              <CryptoIcon
                name={'stUSDS'}
                address={tokensMapping('stUSDS').address}
                size="4"
              />
            }>
            <Heading size="1">
              <Flex gap="xs">
                stUSDS
                <a
                  href={`https://etherscan.io/address/0x99cd4ec3f88a45940936f469e4bb72a2a701eeb9`}
                  target="_blank"
                  rel="noreferrer">
                  <EtherScanPictogram size="0" />
                </a>
              </Flex>
            </Heading>
          </EntityName>
        }
        actions={
          <LinkButton to="https://stusds.herddefi.com/" target="_blank">
            <Flex
              gap="3xs"
              alignItems="center"
              style={{ whiteSpace: 'nowrap' }}>
              stUSDS Utilization Dash
              <ExternalLinkIcon size="1" />
            </Flex>
          </LinkButton>
        }>
        <LinkBanner
          text="About stUSDS"
          linkTo="https://app.sky.money/?network=ethereum&widget=expert">
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            <Heading size="2">About stUSDS</Heading>
            <br />
            stUSDS is a risk capital token that offers higher risk/higher reward
            to fund and support liquidity for SKY stakers. Offering additional
            reward options for SKY stakers encourages more people to participate
            in governance of the Sky Ecosystem, which increases the stability
            and security of the protocol and ecosystem.
          </Box>
        </LinkBanner>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Total Supply"
            variant="stusds"
            field="total_assets"
            data={data}
            options={{
              currency: tokensMapping('stUSDS'),
              notation: 'standard',
              valueOptions: { currencyValueOptions: { iconSize: '3' } }
            }}
          />
        </Flex>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Supply APY"
            data={data}
            field="rate"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
          <KpiCard
            title="Total borrow"
            variant="usds"
            field="borrow"
            data={data}
            options={{
              currency: tokensMapping('USDS'),
              notation: 'standard',
              valueOptions: { currencyValueOptions: { iconSize: '3' } }
            }}
          />

          <KpiCard
            title={
              <Flex
                gap="3xs"
                alignItems="center"
                justifyContent="space-between"
                width="100%">
                <Flex gap="3xs">
                  Utilization Rate 24h
                  <InfoTooltip
                    text={
                      <Flex flexDirection="column">
                        Sky Protocol uses a Time-Weighted Utilization (Utilavg)
                        calculated over a 24-hour period.
                        <br />
                        <br />
                        This ensures stUSDS yield is stable and fair by
                        reflecting all supply and borrow activity.
                      </Flex>
                    }
                  />
                </Flex>
                <ExternalLink href="https://sky-atlas.io/#A.4.4.1.3.2.1.4.1">
                  Learn more
                </ExternalLink>
              </Flex>
            }
            data={data}
            field="utilization_rate_24h"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
            // extra={<InfoTooltip text="TEXT HERE" />}
          />
        </Flex>

        <HistoricTabs />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Borrow APY"
            data={data}
            field="duty_apy"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
          <KpiCard
            title="USDS Deposit cap"
            data={data}
            field="cap"
            options={{
              notation: 'compact'
            }}
          />
          <KpiCard
            title="USDS Debt Ceiling"
            data={data}
            field="line"
            options={{
              notation: 'compact'
            }}
          />
        </Flex>
        {/* <IrmChart currentUtilization={data.utilization} /> */}
        <ActivityCard url={'/stusds/activity/'} linkTo={'activity'} />

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="suppliers"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Suppliers"
                data={data}
                field="depositors"
                options={{
                  linkArrow: true,
                  variants: { valuePercentageValue: 'dynamic' }
                }}
              />
            </LinkWrapper>
            <TopSuppliers flexBasis="100%" />
          </Flex>
          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="3">Suppliers Distribution</Heading>
            <HoldersDonutChart />
          </Flex>
        </Flex>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="4">Borrowers Distribution</Heading>
            <VaultsDonutChart ilk={'LSEV2-SKY-A'} type={'debt'} />
          </Flex>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="/collateral/staking-engine/cdps?what=borrowers"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Borrowers"
                data={data}
                field="number_of_borrowers"
                options={{
                  trend: true,
                  variants: { valuePercentageValue: 'dynamic' },
                  linkArrow: true
                }}
              />
            </LinkWrapper>
            <TopVaults ilk={'LSEV2-SKY-A'} />
          </Flex>
        </Flex>
      </Page>
    );
  }
  return <PageSkeleton />;
}
